import { memo } from "react";
import { CheckIcon } from "lucide-react";

import { ExploreImageChip } from "@/components/tools/ExploreImageChip";

import { positionCategoryToLabel } from "@/lib/constants";
import {
  chamferClipPath,
  formatExploreImageSize,
  isLowResExploreImage,
  isPortraitExploreImage,
} from "@/lib/exploreImages";
import { cn } from "@/lib/utils";
import { ExploreImage } from "@/types";

// One gallery tile at the image's own aspect ratio. Metadata and the focus
// crosshair slide in on hover; the low-res and portrait flags always show.
// While selecting, a checkbox replaces the index and a click toggles it.
export const ExploreImageTile = memo(
  ({
    image,
    index,
    isSelectMode,
    isSelected,
    onClick,
  }: {
    image: ExploreImage;
    index: number;
    isSelectMode: boolean;
    isSelected: boolean;
    onClick: (id: string) => void;
  }) => (
    <button
      className="group relative mb-3 block w-full break-inside-avoid overflow-hidden bg-[#EAEAEA] text-left outline-none ring-[#D17949] focus-visible:ring-2 dark:bg-[#1A1B1E]"
      onClick={() => {
        onClick(image.id);
      }}
      style={{ clipPath: chamferClipPath(14) }}
      type="button"
    >
      <img
        alt={image.caption ?? ""}
        className="block w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        decoding="async"
        loading="lazy"
        src={image.url}
        style={{
          aspectRatio: `${image.width.toString()} / ${image.height.toString()}`,
        }}
      />

      {/* The worker app's card scrim, deepened so the overlay text reads. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[rgba(31,34,35,0.92)] via-[rgba(31,34,35,0.25)] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" />

      {isSelected ? (
        <div className="pointer-events-none absolute inset-0 bg-[#D17949]/15 ring-4 ring-inset ring-[#D17949]" />
      ) : null}

      {isSelectMode ? (
        <span
          className={cn(
            "absolute left-2.5 top-2.5 flex h-6 w-6 items-center justify-center ring-1 ring-inset transition-colors",
            isSelected
              ? "bg-[#D17949] text-white ring-[#D17949]"
              : "bg-black/30 text-transparent ring-white/80 backdrop-blur-sm",
          )}
          style={{ clipPath: chamferClipPath(4) }}
        >
          <CheckIcon className="h-4 w-4" strokeWidth={3} />
        </span>
      ) : (
        <span className="absolute left-3 top-2.5 font-plex-mono text-[10px] tracking-[0.16em] text-white/70 mix-blend-difference">
          {String(index + 1).padStart(3, "0")}
        </span>
      )}

      <div className="absolute right-2 top-2 flex gap-1">
        {isLowResExploreImage(image) && (
          <ExploreImageChip label="Low res" size="sm" tone="copper" />
        )}
        {isPortraitExploreImage(image) && (
          <ExploreImageChip label="Portrait" size="sm" tone="overlayDark" />
        )}
      </div>

      <div
        className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          left: `${image.focus.x.toString()}%`,
          top: `${image.focus.y.toString()}%`,
        }}
      >
        <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#F39562]" />
        <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-[#F39562]" />
        <span className="absolute inset-1.5 rounded-full ring-1 ring-[#F39562]" />
      </div>

      <div className="absolute inset-x-0 bottom-0 flex translate-y-3 flex-col gap-2 p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
        <p
          className={
            image.caption
              ? "line-clamp-2 text-sm font-medium leading-snug text-white"
              : "text-sm italic text-white/50"
          }
        >
          {image.caption ?? "No caption"}
        </p>
        {image.keywords.length > 0 && (
          <p className="truncate font-plex-mono text-[10px] lowercase tracking-[0.08em] text-white/70">
            {image.keywords.slice(0, 3).join(" · ")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1">
          {image.positionCategories.map((category) => (
            <ExploreImageChip
              key={category}
              label={positionCategoryToLabel[category]}
              size="sm"
              tone="overlay"
            />
          ))}
          <span className="ml-auto font-plex-mono text-[10px] tracking-[0.12em] text-white/60">
            {image.width}×{image.height} ·{" "}
            {formatExploreImageSize(image.sizeBytes)}
          </span>
        </div>
      </div>
    </button>
  ),
);
ExploreImageTile.displayName = "ExploreImageTile";
